/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Cài đặt › Module › Tiện ích (ADR-025 §4.6, §8): the add-ons of the build with their state,
 * "Cài đặt" / "Bật" / "Tắt" / "Gỡ cài đặt" (with what happens to the data), the second person's
 * approval where the lifecycle asks for one, the events an add-on has not handled, its settings,
 * and its own pages (drawn from data, `AddonPage`). `?addon=<id>` opens one add-on.
 */

import { ArrowLeft, ArrowRight, Check, Download, Pencil, Plus, Power, PowerOff, RotateCcw, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import {
  ADDON_WAITING_STATUSES,
  type AddonApproval,
  type AddonItem,
  type AddonLifecycleBody,
  type AddonLifecycleResult,
  type AddonNotInBuild,
  type AddonOverview,
  type AddonRecord,
  type AddonRequestKind,
  type AddonRequestOption,
  type AddonRetention,
  type AddonState,
  type AddonWaitingEvent,
  type AddonWaitingStatus,
} from '../../api/areas/addons';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite, WriteError } from '../../components/Write';
import { formatCount, formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { addonMenuChanged } from '../../nav/addonMenu';
import { ChangeDialog, ConfirmCheck, EnvironmentBanner, Panel, ReasonField, Stat } from '../integrations/shared';
import { AddonPages } from './AddonPage';
import { ModuleError, modulesUrl, useCapabilityName, useText } from './ui';

export const STATE_TONE: Readonly<Record<AddonState, Tone>> = {
  NOT_INSTALLED: 'neutral',
  INSTALL_PENDING: 'pending',
  INSTALLED: 'info',
  ACTIVE: 'ok',
  INACTIVE: 'neutral',
  DEGRADED: 'crit',
  UNINSTALL_PENDING: 'pending',
  UNINSTALLED: 'neutral',
};

const WAITING_TONE: Readonly<Record<AddonWaitingStatus, Tone>> = { RETRYING: 'pending', PARKED: 'warn' };

/** `/settings?tab=modules&branch=addons[&addon=…]`. */
export function addonsUrl(addon?: string): string {
  const p = new URLSearchParams({ tab: 'modules', branch: 'addons' });
  if (addon) p.set('addon', addon);
  return `/settings?${p.toString()}`;
}

export function AddonStateBadge({ state }: { state: AddonState }) {
  const { t } = useTranslation('addonModules');
  return (
    <Badge tone={STATE_TONE[state]} title={t(`stateHint.${state}`)}>
      {t(`state.${state}`)}
    </Badge>
  );
}

/** The name of a platform event (`registrar.<aggregate>.<event>.v<n>`): the timeline's words for
 * the event when it has them, else "Sự kiện về <what it concerns>". The subject itself is the
 * secondary line. */
function useEventName(): (subject: string) => string {
  const { t, i18n } = useTranslation('addonModules');
  const loose = i18n.t as unknown as (key: string) => string;
  return (subject) => {
    const [, aggregate = '', type = ''] = subject.split('.');
    const key = `common:labels.event.${type.toUpperCase()}`;
    if (type && i18n.exists(key)) return loose(key);
    const about = `addonModules:events.about.${aggregate}`;
    return t('events.otherEvent', { what: aggregate && i18n.exists(about) ? loose(about) : t('events.about.other') });
  };
}

/** "Gỡ cài đặt (Giữ bản sao mã hoá rồi xoá, 30 ngày)". */
function useChangeText(): (a: Pick<AddonApproval, 'request' | 'retention' | 'retention_days'>) => string {
  const { t } = useTranslation('addonModules');
  return (a) => {
    const request = t(`request.${a.request}`);
    if (!a.retention) return t('approval.change', { request });
    const retention = t(`retention.${a.retention}`);
    return a.retention_days ? t('approval.changeWithDays', { request, retention, count: a.retention_days }) : t('approval.changeWithData', { request, retention });
  };
}

/** What became of the data of an uninstalled add-on. */
function DataFate({ record }: { record: AddonRecord | null }) {
  const { t } = useTranslation('addonModules');
  const [now] = useState(() => Date.now());
  if (!record?.retention) return null;
  if (record.retention.kind === 'KEEP_DATA') return <>{t('card.dataKept')}</>;
  if (record.export_until && Date.parse(record.export_until) > now) return <>{t('card.exportUntil', { date: formatDate(record.export_until) })}</>;
  return <>{t('card.dataDropped')}</>;
}

const REQUEST_ICON: Readonly<Record<AddonRequestKind, typeof Power>> = { install: Download, activate: Power, deactivate: PowerOff, uninstall: Trash2 };

// ---------------------------------------------------------------------------------------------
// Lifecycle requests
// ---------------------------------------------------------------------------------------------

type Dialog =
  | { kind: 'request'; option: AddonRequestOption }
  | { kind: 'apply'; approval: AddonApproval }
  | { kind: 'settings' }
  | null;

/**
 * "Cài đặt", "Bật", "Tắt", "Gỡ cài đặt" (with the data's fate), or the change another person
 * approved: sent with a reason; a change that needs a second person is filed for approval first.
 */
function LifecycleForm({
  item,
  env,
  option,
  approval,
  onClose,
  onDone,
}: {
  item: AddonItem;
  env: IntegrationEnvironment;
  option: AddonRequestOption | null;
  approval: AddonApproval | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const change = useChangeText();
  const request: AddonRequestKind = approval?.request ?? option?.request ?? 'install';
  const [retention, setRetention] = useState<AddonRetention>(approval?.retention ?? 'KEEP_DATA');
  const [days, setDays] = useState(String(approval?.retention_days ?? 30));
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: AddonLifecycleBody, opts) => api.requestAddonChange(item.manifest.id, body, opts));
  const daysValue = Number(days);
  const daysOk = retention !== 'EXPORT_THEN_DROP' || (Number.isInteger(daysValue) && daysValue >= 1 && daysValue <= 3650);
  const fourEyes = !approval && (request === 'uninstall' && retention === 'DROP' ? !!option?.drop_four_eyes || !!option?.four_eyes : !!option?.four_eyes);
  const destructive = request === 'uninstall' || request === 'deactivate';
  if (write.state.phase === 'done') return <LifecycleOutcome result={write.state.result} onClose={onClose} />;
  const describe = request === 'activate' && item.state === 'INSTALLED' ? [t('dialog.activate'), t('dialog.activateFirst')] : [t(`dialog.${request}`)];
  const submit = () =>
    void write
      .run({
        request,
        ...(request === 'uninstall' ? { retention, ...(retention === 'EXPORT_THEN_DROP' ? { retention_days: daysValue } : {}) } : {}),
        reason: reason.trim(),
        ...(approval ? { approval_request_id: approval.id } : {}),
      })
      .then((r) => {
        if (r) onDone();
      });
  return (
    <div className="space-y-4" data-addon-request={request}>
      <EnvironmentBanner
        env={env}
        approvalRequired={fourEyes}
        message={
          approval
            ? t('dialog.applyApproved')
            : fourEyes
              ? request === 'uninstall' && retention === 'DROP' && !option?.four_eyes
                ? t('dialog.fourEyesDrop')
                : t('dialog.fourEyes')
              : t('dialog.noApproval')
        }
      />
      <p className="text-sm font-medium">{text(item.manifest.name)}</p>
      {approval ? <Callout tone="ok">{change(approval)}</Callout> : null}
      {describe.map((d) => (
        <p key={d} className="text-sm">
          {d}
        </p>
      ))}
      {request === 'uninstall' && !approval ? (
        <fieldset className="space-y-2">
          {(['KEEP_DATA', 'EXPORT_THEN_DROP', 'DROP'] as const).map((r) => (
            <label key={r} aria-label={t(`retention.${r}`)} className="flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm has-[:checked]:border-primary">
              <input type="radio" name="retention" value={r} checked={retention === r} onChange={() => setRetention(r)} className="mt-1" />
              <span className="grid gap-0.5">
                <span className="font-medium">{t(`retention.${r}`)}</span>
                <span className="text-xs text-muted-foreground">{t(`retentionHint.${r}`)}</span>
              </span>
            </label>
          ))}
          {retention === 'EXPORT_THEN_DROP' ? (
            <Field label={t('dialog.retentionDays')} hint={t('dialog.retentionDaysHint')}>
              <Input type="number" inputMode="numeric" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} aria-invalid={!daysOk} />
            </Field>
          ) : null}
        </fieldset>
      ) : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('dialog.cancel')}
        </Button>
        <Button type="button" variant={destructive || env === 'PRODUCTION' ? 'destructive' : 'default'} disabled={!reason.trim() || !confirmed || !daysOk || write.busy} onClick={submit}>
          {approval ? <Check /> : null}
          {approval ? t('approval.apply') : fourEyes ? t('dialog.submitForApproval') : t(`request.${request}`)}
        </Button>
      </DialogFooter>
    </div>
  );
}

function LifecycleOutcome({ result, onClose }: { result: AddonLifecycleResult; onClose: () => void }) {
  const { t } = useTranslation('addonModules');
  return (
    <div className="space-y-4" role="status">
      {result.result === 'APPROVAL_REQUESTED' ? (
        <Callout tone="pending">{t('dialog.requested')}</Callout>
      ) : (
        <Callout tone="ok">
          {t('dialog.applied', { state: t(`state.${result.state}`) })} {result.storage_step ? t('dialog.storageStep') : null}
        </Callout>
      )}
      <DialogFooter>
        <Button onClick={onClose}>{t('dialog.close')}</Button>
      </DialogFooter>
    </div>
  );
}

/** "Tạo cài đặt": the add-on's settings as the module instance `default` (ADR-025 §8). */
function SettingsForm({ item, env, onClose, onDone }: { item: AddonItem; env: IntegrationEnvironment; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('addonModules');
  const [reason, setReason] = useState('');
  const write = useWrite((body: { key: string; reason: string }, opts) => api.createModuleInstance('addons', item.manifest.id, body, opts));
  if (write.state.phase === 'done') {
    return (
      <div className="space-y-4" role="status">
        <Callout tone="ok">{t('settingsDialog.created')}</Callout>
        <DialogFooter>
          <Button asChild>
            <Link to={modulesUrl('addons', item.manifest.id, 'default', 'config')}>
              <Pencil /> {t('card.settingsOpen')}
            </Link>
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} approvalRequired={false} message={t('settingsDialog.description')} />
      <ReasonField value={reason} onChange={setReason} />
      {write.state.phase === 'error' ? <ModuleError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('dialog.cancel')}
        </Button>
        <Button
          type="button"
          disabled={!reason.trim() || write.busy}
          onClick={() =>
            void write.run({ key: 'default', reason: reason.trim() }).then((r) => {
              if (r) onDone();
            })
          }
        >
          <Plus /> {t('settingsDialog.submit')}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// One add-on
// ---------------------------------------------------------------------------------------------

function ApprovalNote({ approval, canManage, onApply }: { approval: AddonApproval; canManage: boolean; onApply: () => void }) {
  const { t } = useTranslation('addonModules');
  const change = useChangeText();
  const link = (
    <Link to={`/admin/approvals?request=${encodeURIComponent(approval.id)}`} className="font-medium text-primary hover:underline">
      {t('approval.open')}
    </Link>
  );
  if (approval.status === 'PENDING') {
    return (
      <Callout tone="pending">
        {t('approval.pending', { change: change(approval) })} {approval.requested_by_self ? t('approval.pendingSelf') : null} {link}
      </Callout>
    );
  }
  return (
    <Callout tone="ok">
      <span className="flex flex-wrap items-center gap-2">
        <span>{t('approval.approved', { change: change(approval) })}</span>
        {canManage ? (
          <Button size="xs" onClick={onApply}>
            <Check /> {t('approval.apply')}
          </Button>
        ) : null}
        {link}
      </span>
    </Callout>
  );
}

/**
 * One add-on of the build: name, provider, version, what it can do, its state and what may be
 * asked of it now, an approval waiting, its settings and the events it has not handled.
 */
export function AddonCard({ item, overview, onChanged, detail = false }: { item: AddonItem; overview: AddonOverview; onChanged: () => void; detail?: boolean }) {
  const { t } = useTranslation('addonModules');
  const text = useText();
  const capability = useCapabilityName();
  const [, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<Dialog>(null);
  const m = item.manifest;
  const env = overview.environment;
  const close = () => setDialog(null);
  const busy = item.approval !== null;
  const waiting = item.waiting.parked + item.waiting.retrying;
  const settingsLine = !m.settings.fields.length
    ? t('card.noSettings')
    : !item.settings.exists
      ? t('card.settingsNone')
      : item.settings.in_force
        ? t('card.settingsSaved')
        : t('card.settingsDraft');
  const requestTitle = (r: AddonRequestKind) => t('dialog.title', { request: t(`request.${r}`), name: text(m.name) });
  return (
    <Panel
      title={
        <span className="flex flex-wrap items-center gap-2">
          {text(m.name)}
          <Badge tone="neutral" icon={false}>
            {t('card.version', { version: m.version })}
          </Badge>
          <AddonStateBadge state={item.state} />
        </span>
      }
      description={text(m.summary)}
      actions={
        detail ? null : (
          <Button size="sm" variant="ghost" onClick={() => setParams({ branch: 'addons', addon: m.id })}>
            {t('card.open')} <ArrowRight />
          </Button>
        )
      }
    >
      <div className="space-y-4" data-addon={m.id}>
        <p className="text-sm text-muted-foreground">{t(`stateHint.${item.state}`)}</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label={t('card.vendor')}>
            <a href={m.vendor.website} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline">
              {m.vendor.name}
            </a>
          </Stat>
          <Stat label={t('card.installed')}>{item.record?.version ?? <span className="text-muted-foreground">{t('card.notInstalled')}</span>}</Stat>
          <Stat label={t('card.capabilities')}>
            <span className="flex flex-wrap gap-1">
              {m.capabilities.map((c) => (
                <Badge key={c} tone="info" icon={false}>
                  {capability(c)}
                </Badge>
              ))}
            </span>
          </Stat>
          <Stat label={t('card.settings')}>
            <span className="grid gap-1">
              <span>{settingsLine}</span>
              {m.settings.fields.length && overview.can_manage ? (
                item.settings.exists ? (
                  <Link to={modulesUrl('addons', m.id, 'default')} className="font-medium text-primary hover:underline">
                    {t('card.settingsOpen')}
                  </Link>
                ) : (
                  <Button size="xs" variant="outline" className="justify-self-start" onClick={() => setDialog({ kind: 'settings' })}>
                    <Plus /> {t('card.settingsCreate')}
                  </Button>
                )
              ) : null}
            </span>
          </Stat>
          <Stat label={t('card.waiting')}>
            {waiting ? t('card.waitingCount', { parked: formatCount(item.waiting.parked), retrying: formatCount(item.waiting.retrying) }) : t('card.waitingNone')}
          </Stat>
        </div>
        {!m.production_ready ? <Callout tone="warn">{t('card.notProductionReady')}</Callout> : null}
        {item.state === 'DEGRADED' ? (
          <Callout tone="crit">{item.record?.degraded_reason ? t('card.degraded', { reason: item.record.degraded_reason }) : t('card.degradedNoReason')}</Callout>
        ) : null}
        {item.state === 'UNINSTALLED' ? (
          <p className="text-sm">
            <DataFate record={item.record} />
          </p>
        ) : null}
        {item.approval ? <ApprovalNote approval={item.approval} canManage={overview.can_manage} onApply={() => item.approval && setDialog({ kind: 'apply', approval: item.approval })} /> : null}
        {overview.can_manage ? (
          <div className="flex flex-wrap gap-2">
            {item.requests.length ? (
              item.requests.map((o) => {
                const Icon = REQUEST_ICON[o.request];
                return (
                  <Button
                    key={o.request}
                    size="sm"
                    variant={o.request === 'uninstall' ? 'destructive' : o.request === 'deactivate' ? 'outline' : 'default'}
                    // While a change waits for a second person, only changes that need none (switching off) go ahead.
                    disabled={(busy && (o.four_eyes || o.request === 'uninstall')) || (o.request === 'install' && !item.installable_here)}
                    onClick={() => setDialog({ kind: 'request', option: o })}
                  >
                    <Icon /> {t(`request.${o.request}`)}
                  </Button>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">{t('card.noRequest')}</p>
            )}
          </div>
        ) : null}
      </div>
      <ChangeDialog open={dialog?.kind === 'request'} onOpenChange={(o) => !o && close()} title={dialog?.kind === 'request' ? requestTitle(dialog.option.request) : ''}>
        {dialog?.kind === 'request' ? <LifecycleForm item={item} env={env} option={dialog.option} approval={null} onClose={close} onDone={onChanged} /> : null}
      </ChangeDialog>
      <ChangeDialog open={dialog?.kind === 'apply'} onOpenChange={(o) => !o && close()} title={dialog?.kind === 'apply' ? requestTitle(dialog.approval.request) : ''}>
        {dialog?.kind === 'apply' ? <LifecycleForm item={item} env={env} option={null} approval={dialog.approval} onClose={close} onDone={onChanged} /> : null}
      </ChangeDialog>
      <ChangeDialog open={dialog?.kind === 'settings'} onOpenChange={(o) => !o && close()} title={t('settingsDialog.title', { name: text(m.name) })}>
        {dialog?.kind === 'settings' ? <SettingsForm item={item} env={env} onClose={close} onDone={onChanged} /> : null}
      </ChangeDialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Waiting events
// ---------------------------------------------------------------------------------------------

function ReplayForm({ addon, event, env, onClose, onDone }: { addon: string; event: AddonWaitingEvent; env: IntegrationEnvironment; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('addonModules');
  const name = useEventName();
  const [reason, setReason] = useState('');
  const write = useWrite((body: { reason: string }, opts) => api.replayAddonEvent(addon, event.event_id, body, opts));
  if (write.state.phase === 'done') {
    return (
      <div className="space-y-4" role="status">
        <Callout tone="ok">{t('events.replayDone')}</Callout>
        <DialogFooter>
          <Button onClick={onClose}>{t('dialog.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} approvalRequired={false} message={t('events.replayDescription')} />
      <p className="text-sm font-medium">{name(event.subject)}</p>
      <ReasonField value={reason} onChange={setReason} />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('dialog.cancel')}
        </Button>
        <Button
          type="button"
          disabled={!reason.trim() || write.busy}
          onClick={() =>
            void write.run({ reason: reason.trim() }).then((r) => {
              if (r) onDone();
            })
          }
        >
          <RotateCcw /> {t('events.replay')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** The events an add-on has not handled: being retried, or held back until someone sends them again. */
function EventsPanel({ item, overview, onChanged }: { item: AddonItem; overview: AddonOverview; onChanged: () => void }) {
  const { t } = useTranslation('addonModules');
  const name = useEventName();
  const [status, setStatus] = useState<AddonWaitingStatus | ''>('');
  const [tick, setTick] = useState(0);
  const [replaying, setReplaying] = useState<AddonWaitingEvent | null>(null);
  const addon = item.manifest.id;
  const result = useApi((signal) => api.listAddonEvents(addon, { status: status || undefined, limit: 100 }, { signal }), [addon, status, tick]);
  const rows = useRetained(result.data)?.items;
  const columns: Column<AddonWaitingEvent>[] = [
    {
      key: 'event',
      header: t('events.columns.event'),
      className: 'whitespace-normal',
      cell: (e) => (
        <span className="grid gap-0.5">
          <span className="font-medium">{name(e.subject)}</span>
          <code className="text-xs text-muted-foreground">{e.subject}</code>
        </span>
      ),
    },
    { key: 'occurred', header: t('events.columns.occurred'), cell: (e) => formatDateTime(e.occurred_at) },
    {
      key: 'status',
      header: t('events.columns.status'),
      cell: (e) => (
        <Badge tone={WAITING_TONE[e.status]} icon={false}>
          {t(`events.status.${e.status}`)}
        </Badge>
      ),
    },
    { key: 'attempts', header: t('events.columns.attempts'), numeric: true, cell: (e) => formatCount(e.attempts) },
    { key: 'next', header: t('events.columns.next'), cell: (e) => (e.next_attempt_at ? formatDateTime(e.next_attempt_at) : '—') },
    { key: 'reason', header: t('events.columns.reason'), className: 'whitespace-normal text-xs text-muted-foreground', cell: (e) => e.reason ?? '—' },
    {
      key: 'actions',
      header: '',
      cell: (e) =>
        e.status === 'PARKED' && overview.can_operate ? (
          <Button size="xs" variant="outline" onClick={() => setReplaying(e)}>
            <RotateCcw /> {t('events.replay')}
          </Button>
        ) : null,
    },
  ];
  const filter = (
    <span className="flex flex-wrap gap-1" role="group" aria-label={t('events.columns.status')}>
      {(['', ...ADDON_WAITING_STATUSES] as const).map((s) => (
        <Button key={s || 'all'} size="xs" variant={status === s ? 'default' : 'outline'} onClick={() => setStatus(s)}>
          {s ? t(`events.status.${s}`) : t('events.filterAll')}
        </Button>
      ))}
    </span>
  );
  return (
    <Panel title={t('events.title')} description={t('events.hint')} actions={filter} flush>
      {result.status === 'error' && !rows ? (
        <div className="px-5 pb-4">
          <DataError error={result.error} onRetry={result.reload} title={t('events.loadFailed')} />
        </div>
      ) : !rows ? (
        <div className="px-5">
          <Loading rows={2} />
        </div>
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(e) => e.event_id} empty={<p className="px-5 py-4 text-sm text-muted-foreground">{t('events.none')}</p>} />
      )}
      <ChangeDialog open={replaying !== null} onOpenChange={(o) => !o && setReplaying(null)} title={t('events.replayTitle')}>
        {replaying ? (
          <ReplayForm
            addon={addon}
            event={replaying}
            env={overview.environment}
            onClose={() => setReplaying(null)}
            onDone={() => {
              setTick((n) => n + 1);
              onChanged();
            }}
          />
        ) : null}
      </ChangeDialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------------------------

function NotInBuildPanel({ rows }: { rows: readonly AddonNotInBuild[] }) {
  const { t } = useTranslation('addonModules');
  const columns: Column<AddonNotInBuild>[] = [
    { key: 'addon', header: t('list.columns.addon'), cell: (r) => <code className="text-xs">{r.addon}</code> },
    { key: 'state', header: t('list.columns.state'), cell: (r) => <AddonStateBadge state={r.state} /> },
    { key: 'data', header: t('list.columns.data'), className: 'whitespace-normal', cell: (r) => <DataFate record={r.record} /> },
    { key: 'updated', header: t('list.columns.updated'), cell: (r) => formatDateTime(r.record.updated_at) },
  ];
  return (
    <Panel title={t('list.notInBuildTitle')} description={t('list.notInBuildHint')} flush>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.addon} />
    </Panel>
  );
}

function Detail({ item, overview, onChanged, back }: { item: AddonItem; overview: AddonOverview; onChanged: () => void; back: ReactNode }) {
  return (
    <div className="space-y-6" data-module-branch="addons">
      {back}
      <AddonCard item={item} overview={overview} onChanged={onChanged} detail />
      <AddonPages addon={item.manifest.id} state={item.state} env={overview.environment} />
      <EventsPanel item={item} overview={overview} onChanged={onChanged} />
    </div>
  );
}

/** The "Tiện ích" tab of Cài đặt › Module. */
export function AddonsTab() {
  const { t } = useTranslation('addonModules');
  const { t: tm } = useTranslation('modules');
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const result = useApi((signal) => api.getAddons({ signal }), [tick]);
  const data = useRetained(result.data);
  // A change may switch an add-on on or off: its entries in the navigation follow.
  const reload = () => {
    setTick((n) => n + 1);
    addonMenuChanged();
  };
  if (result.status === 'error' && !data) return <DataError error={result.error} onRetry={result.reload} title={t('list.loadFailed')} />;
  if (!data) return <Loading rows={4} />;
  const opened = params.get('addon');
  const item = opened ? data.items.find((i) => i.manifest.id === opened) : undefined;
  if (item) {
    const back = (
      <Button size="sm" variant="ghost" onClick={() => setParams({ branch: 'addons' })}>
        <ArrowLeft /> {t('list.back')}
      </Button>
    );
    return <Detail item={item} overview={data} onChanged={reload} back={back} />;
  }
  return (
    <div className="space-y-6" data-module-branch="addons">
      <p className="text-sm text-muted-foreground">{tm('branchHint.addons')}</p>
      {!data.worker_configured && data.items.length ? <Callout tone="warn">{t('list.workerMissing')}</Callout> : null}
      {data.items.length ? (
        data.items.map((i) => <AddonCard key={i.manifest.id} item={i} overview={data} onChanged={reload} />)
      ) : (
        <EmptyState title={t('list.none')}>{t('list.noneHint')}</EmptyState>
      )}
      {data.not_in_build.length ? <NotInBuildPanel rows={data.not_in_build} /> : null}
    </div>
  );
}
